import { useCallback, useContext, useEffect, useState } from "react";

import {
  getSatelliteDeviceModel,
  getSatelliteDeviceModelLabel,
} from "../constants";
import {
  createSatelliteDevice,
  deleteSatelliteDevice,
  getSatelliteDevicesByOrgId,
  updateSatelliteDevice,
} from "api/endpoints/lone-worker";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

export interface SatelliteDeviceFormValues {
  imei: string;
  model: string;
  notes: string;
}

export interface SatelliteDevice extends SatelliteDeviceFormValues {
  id: string;
}

export type SatelliteDeviceField = keyof SatelliteDeviceFormValues;

type SatelliteDeviceTouched = Record<SatelliteDeviceField, boolean>;

const EMPTY_FORM: SatelliteDeviceFormValues = {
  imei: "",
  model: "",
  notes: "",
};

const UNTOUCHED: SatelliteDeviceTouched = {
  imei: false,
  model: false,
  notes: false,
};

export interface UseSatelliteDevicesParams {
  orgId: string;
  regionCode: string;
}

export interface UseSatelliteDevices {
  devices: SatelliteDevice[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  isAdding: boolean;
  isSubmitting: boolean;
  editingIndex: number | null;
  formValues: SatelliteDeviceFormValues;
  touched: SatelliteDeviceTouched;
  canSubmit: boolean;
  searchInput: string;
  hasActiveSearch: boolean;
  setSearchInput: (value: string) => void;
  applySearch: () => void;
  clearSearch: () => void;
  startAdding: () => void;
  cancelAdding: () => void;
  confirmAdding: () => void;
  startEditing: (index: number) => void;
  removeDevice: (index: number) => Promise<void>;
  updateField: (field: SatelliteDeviceField, value: string) => void;
  touchField: (field: SatelliteDeviceField) => void;
}

export const useSatelliteDevices = ({
  orgId,
  regionCode,
}: UseSatelliteDevicesParams): UseSatelliteDevices => {
  const { showError } = useContext(SnackbarContext);
  const [devices, setDevices] = useState<SatelliteDevice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageToken, setNextPageToken] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [formValues, setFormValues] =
    useState<SatelliteDeviceFormValues>(EMPTY_FORM);
  const [touched, setTouched] = useState<SatelliteDeviceTouched>(UNTOUCHED);

  const fetchDevices = useCallback(() => {
    if (!orgId) {
      return;
    }
    setIsLoading(true);
    getSatelliteDevicesByOrgId(
      orgId,
      "",
      appliedSearch,
      callOptionsHeaders(regionCode, orgId),
    )
      .then((res) => {
        setDevices(
          res.devices.map((device) => ({
            id: device.id,
            imei: device.serialNumber,
            model: getSatelliteDeviceModelLabel(device.model),
            notes: device.notes,
          })),
        );
        setNextPageToken(res.nextPageToken);
      })
      .catch((error: GrpcError) => showError(error))
      .finally(() => setIsLoading(false));
  }, [orgId, regionCode, appliedSearch, showError]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const loadMore = () => {
    if (!orgId || !nextPageToken || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    getSatelliteDevicesByOrgId(
      orgId,
      nextPageToken,
      appliedSearch,
      callOptionsHeaders(regionCode, orgId),
    )
      .then((res) => {
        setDevices((prev) => [
          ...prev,
          ...res.devices.map((device) => ({
            id: device.id,
            imei: device.serialNumber,
            model: getSatelliteDeviceModelLabel(device.model),
            notes: device.notes,
          })),
        ]);
        setNextPageToken(res.nextPageToken);
      })
      .catch((error: GrpcError) => showError(error))
      .finally(() => setIsLoadingMore(false));
  };

  const applySearch = () => {
    setAppliedSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setAppliedSearch("");
  };

  const startAdding = () => {
    setFormValues(EMPTY_FORM);
    setTouched(UNTOUCHED);
    setEditingIndex(null);
    setIsAdding(true);
  };

  const startEditing = (index: number) => {
    setFormValues(devices[index]);
    setTouched(UNTOUCHED);
    setEditingIndex(index);
    setIsAdding(true);
  };

  const cancelAdding = () => {
    setFormValues(EMPTY_FORM);
    setTouched(UNTOUCHED);
    setEditingIndex(null);
    setIsAdding(false);
  };

  const updateField = (field: SatelliteDeviceField, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const touchField = (field: SatelliteDeviceField) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const resetForm = () => {
    setFormValues(EMPTY_FORM);
    setTouched(UNTOUCHED);
    setEditingIndex(null);
    setIsAdding(false);
  };

  const confirmAdding = () => {
    const formInput: SatelliteDeviceFormValues = {
      imei: formValues.imei.trim(),
      model: formValues.model.trim(),
      notes: formValues.notes.trim(),
    };

    if (editingIndex !== null) {
      const editingDevice = devices[editingIndex];
      setIsSubmitting(true);
      updateSatelliteDevice(
        editingDevice.id,
        formInput.notes,
        callOptionsHeaders(regionCode, orgId),
      )
        .then(() => {
          setDevices((prev) =>
            prev.map((item) =>
              item.id === editingDevice.id
                ? { ...item, notes: formInput.notes }
                : item,
            ),
          );
          resetForm();
        })
        .catch((error: GrpcError) => showError(error))
        .finally(() => setIsSubmitting(false));
      return;
    }

    setIsSubmitting(true);
    createSatelliteDevice(
      orgId,
      formInput.imei,
      getSatelliteDeviceModel(formInput.model),
      formInput.notes,
      callOptionsHeaders(regionCode, orgId),
    )
      .then((res) => {
        setDevices((prev) => [
          {
            id: res.id,
            imei: formInput.imei,
            model: formInput.model,
            notes: formInput.notes,
          },
          ...prev,
        ]);
        resetForm();
      })
      .catch((error: GrpcError) => showError(error))
      .finally(() => setIsSubmitting(false));
  };

  const removeDevice = (index: number) => {
    const device = devices[index];
    if (!device) {
      return Promise.resolve();
    }
    setIsSubmitting(true);
    return deleteSatelliteDevice(
      device.id,
      callOptionsHeaders(regionCode, orgId),
    )
      .then(() => {
        setDevices((prev) => prev.filter((item) => item.id !== device.id));
      })
      .catch((error: GrpcError) => showError(error))
      .finally(() => setIsSubmitting(false));
  };

  const canSubmit =
    formValues.imei.trim().length > 0 && formValues.model.trim().length > 0;

  return {
    devices,
    isLoading,
    isLoadingMore,
    hasMore: nextPageToken.length > 0,
    loadMore,
    isAdding,
    isSubmitting,
    editingIndex,
    formValues,
    touched,
    canSubmit,
    searchInput,
    hasActiveSearch: appliedSearch.length > 0,
    setSearchInput,
    applySearch,
    clearSearch,
    startAdding,
    cancelAdding,
    confirmAdding,
    startEditing,
    removeDevice,
    updateField,
    touchField,
  };
};
